import React from 'react';
import { Button } from 'antd';
import { Link } from 'react-router-dom';

/**
 * A record that cannot be shown (docs/platform-standards.md §14, "Resource
 * states"; KPR-1579): not found, no access, or a read that failed. One panel
 * in the page's place — the page's own bar stays above it — with what
 * happened, one line of what to do, and the way back.
 *
 * The copy is the CALLER's: what each surface says is ruled per surface (a tag
 * and a list are "not found" whatever the reason, the collection shell names
 * both reasons in one sentence, a chart says its own d26 sentence), so this
 * draws words it is given and never composes them.
 *
 *   <ResourceState state="not-found" title="List not found" body="…" backTo="/" backLabel="Go to Home" />
 *
 * - `state`: 'not-found' | 'no-access' | 'error' (`resourceStateOf` below).
 *   Anything else draws nothing.
 * - `title` is an `h2`; leave it out when the page's bar already titles the
 *   failure (its `h1`), so the name is not said twice.
 * - `backTo` + `backLabel`: the way back, a link. On `error` with `onRetry`,
 *   **Retry** is the one filled primary and the way back steps down to default.
 * - Further props (`className`, `data-*`) go on the panel: a scope's own hook
 *   (`data-shell-state-message`, components/shell/ShellState) rides along.
 *
 * It is a polite live region, and never shows a raw exception message.
 */
export const RESOURCE_STATES = Object.freeze(['not-found', 'no-access', 'error']);

/**
 * Which state a failed read is. The API answers 404 for a record that does not
 * exist AND for one the person may not open (it never confirms a hidden one),
 * so `goneCodes` (404 by default) are not-found; `noAccessCodes` (403 by
 * default) are a known record that is refused — pass `[]` where a surface
 * treats a refusal as a failed read; anything else is an error to retry. No
 * error is 'ready'.
 */
export function resourceStateOf(error, { goneCodes = [404], noAccessCodes = [403] } = {}) {
	if (!error) return 'ready';
	const status = error?.response?.status;
	if (goneCodes.includes(status)) return 'not-found';
	if (noAccessCodes.includes(status)) return 'no-access';
	return 'error';
}

export default function ResourceState({ state, title = null, body = null, backTo = null, backLabel = null, onRetry = null, className = '', ...rest }) {
	if (!RESOURCE_STATES.includes(state)) return null;
	const retry = state === 'error' && typeof onRetry === 'function';
	return (
		<section
			className={['resource-state', className].filter(Boolean).join(' ')}
			role="status"
			aria-live="polite"
			data-resource-state={state}
			{...rest}
		>
			{title ? <h2 className="resource-state__title">{title}</h2> : null}
			{body ? <p className="resource-state__body">{body}</p> : null}
			{retry || (backTo && backLabel) ? (
				<div className="resource-state__actions">
					{retry ? <Button type="primary" onClick={onRetry}>Retry</Button> : null}
					{backTo && backLabel ? (
						<Link to={backTo} className={retry ? 'ant-btn ant-btn-default' : 'ant-btn ant-btn-primary'}>
							<span>{backLabel}</span>
						</Link>
					) : null}
				</div>
			) : null}
		</section>
	);
}
